import React, { useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { isEmpty } from 'lodash'
import pluralize from 'pluralize'

import { Button } from '@/components/buttons/Button'
import { BasicModal } from '@/components/layout/modals/BasicModal'
import { MultiSelect } from '@/components/forms/MultiSelect'
import { ModalActions } from '@/components/modals/ModalActions'
import Select from '@/components/forms/Select'
import { ActionsDropdown } from '@/components/dropdowns/ActionsDropdown'
import {
  bulkUpdatePermissions,
  bulkAssignTags,
  bulkAssignPlaybook,
  bulkActivateUsers,
  bulkDeactivateUsers,
} from '@/reducers/organizations/organizationUsers.actions'
import { cloudAccessSchema } from '../organization.schema'
import { SupportActionsModal } from './SupportActionsModal'

export const BulkUserActions = ({
  selectedUserIds,
  selectedUsers,
  organizationId,
  playbooks,
  tags,
  tagCategories,
  handleDeselectAll,
  fetchUsers,
  userFilter,
}) => {
  const dispatch = useDispatch()
  const currentUser = useSelector((state) => state.currentUser)
  const outboundCampaignsEnabled = useSelector(
    (state) => state.organization.outbound_campaigns_enabled
  )
  const togoEnabled = useSelector((state) => state.organization.togo_enabled)
  const webhooksEnabled = useSelector((state) => state.organization.webhooks_enabled)
  const agentGPTEnabled = useSelector((state) => state.organization.agent_gpt_enabled)
  const isBaltoAdmin = currentUser.organizationid === 1
  const [activeModal, setActiveModal] = useState(null)
  const [selectedPermissions, setSelectedPermissions] = useState([])
  const [categoryTags, setCategoryTags] = useState({})
  const [uncategorizedTags, setUncategorizedTags] = useState([])
  const [selectedPlaybook, setSelectedPlaybook] = useState(null)
  const [submitting, setSubmitting] = useState(false)

  const permissionOptions = useMemo(() => {
    return cloudAccessSchema.options.filter((option) => {
      if (option.value === 'prompt_editor' && !isBaltoAdmin) {
        return false
      }

      if (option.value === 'outbound_campaign_editor' && !outboundCampaignsEnabled) {
        return false
      }

      if (option.value === 'togo_editor' && !togoEnabled) {
        return false
      }

      if (option.value === 'webhook_editor' && !webhooksEnabled) {
        return false
      }

      if (option.value === 'verified_answer_editor' && !agentGPTEnabled) {
        return false
      }

      return true
    })
  }, [isBaltoAdmin, outboundCampaignsEnabled, togoEnabled, webhooksEnabled, agentGPTEnabled])

  const closeModal = () => {
    setSubmitting(false)
    setActiveModal(null)
    setSelectedPermissions([])
    setCategoryTags({})
    setUncategorizedTags([])
    setSelectedPlaybook(null)
  }

  const refreshUsers = () => {
    dispatch(fetchUsers({ organizationId, queryParam: userFilter }))
  }

  const handlePermissionsSubmit = () => {
    setSubmitting(true)
    dispatch(
      bulkUpdatePermissions({
        userIds: selectedUserIds,
        permissions: selectedPermissions,
        organizationId,
        onSuccess: () => {
          refreshUsers()
          handleDeselectAll()
          closeModal()
        },
      })
    )
  }

  const handleTagsSubmit = () => {
    const tagIds = [
      ...Object.values(categoryTags).filter(Boolean),
      ...uncategorizedTags.map((tag) => tag.value),
    ]

    if (isEmpty(tagIds)) {
      return
    }

    setSubmitting(true)
    dispatch(
      bulkAssignTags({
        userIds: selectedUserIds,
        tagIds,
        organizationId,
        onSuccess: () => {
          refreshUsers()
          handleDeselectAll()
          closeModal()
        },
      })
    )
  }

  const hasTagsSelected = !isEmpty(uncategorizedTags) || Object.values(categoryTags).some(Boolean)

  const handlePlaybookSubmit = () => {
    setSubmitting(true)
    dispatch(
      bulkAssignPlaybook({
        userIds: selectedUserIds,
        configCid: selectedPlaybook,
        onSuccess: () => {
          refreshUsers()
          handleDeselectAll()
          closeModal()
        },
      })
    )
  }

  const handleActivateUsers = () => {
    dispatch(
      bulkActivateUsers({
        userIds: selectedUserIds,
        organizationId,
        onSuccess: () => {
          refreshUsers()
          handleDeselectAll()
        },
      })
    )
  }

  const handleDeactivateUsers = () => {
    setActiveModal('confirmDeactivate')
  }

  const confirmDeactivateUsers = () => {
    setSubmitting(true)
    dispatch(
      bulkDeactivateUsers({
        userIds: selectedUserIds,
        organizationId,
        onSuccess: () => {
          refreshUsers()
          handleDeselectAll()
          closeModal()
        },
      })
    )
  }

  const selectedCount = selectedUserIds.length
  const playbookOptions = playbooks.map((playbook) => ({
    label: playbook.name,
    value: playbook.cid,
  }))

  const uncategorizedTagOptions = tags
    .filter((tag) => !tagCategories.some((category) => category.id === tag.tag_category_id))
    .map((tag) => ({ value: tag.id, label: tag.name }))

  return (
    <div className="flex-align-center medium-gap">
      <ActionsDropdown
        triggerLabel="Update Status"
        dataTestId="bulk-status-button"
        actions={[
          {
            label: 'Activate Users',
            onClick: handleActivateUsers,
            testId: 'bulk-activate',
          },
          {
            label: 'Deactivate Users',
            onClick: handleDeactivateUsers,
            testId: 'bulk-deactivate',
            isDanger: true,
          },
        ]}
      />
      <Button
        small
        onClick={() => setActiveModal('tags')}
        data-testid="bulk-tags-button"
        disabled={isEmpty(tags)}
        disabledTooltip="No tags have been created for this organization"
      >
        Set Tags
      </Button>
      <Button
        small
        onClick={() => setActiveModal('playbook')}
        data-testid="bulk-playbook-button"
        disabled={isEmpty(playbooks)}
        disabledTooltip="No playbooks have been created for this organization"
      >
        Reassign Playbook
      </Button>
      <Button
        small
        onClick={() => setActiveModal('permissions')}
        data-testid="bulk-permissions-button"
      >
        Add Permissions
      </Button>
      {isBaltoAdmin && (
        <Button
          small
          onClick={() => setActiveModal('supportActions')}
          data-testid="bulk-support-actions-button"
        >
          Support Actions
        </Button>
      )}

      {activeModal === 'permissions' && (
        <BasicModal
          title={`Add Permissions for ${pluralize('User', selectedCount, true)}`}
          onClose={closeModal}
          show
          size="tiny"
        >
          <div className="mb">
            <label className="label-style">Select Permissions</label>
            <MultiSelect
              condenseOptions={false}
              showFullOptions
              aria-label="Select Permissions"
              placeholder="Select Permissions..."
              options={permissionOptions}
              value={selectedPermissions}
              onChange={(value) => setSelectedPermissions(value)}
            />
          </div>
          <div className="modal-footer">
            <Button onClick={closeModal}>Cancel</Button>
            <Button
              brand
              disabled={isEmpty(selectedPermissions) || submitting}
              loading={submitting}
              onClick={handlePermissionsSubmit}
              data-testid="bulk-permissions-apply"
            >
              Apply
            </Button>
          </div>
        </BasicModal>
      )}

      {activeModal === 'tags' && (
        <BasicModal
          title={`Set Tags for ${pluralize('User', selectedCount, true)}`}
          onClose={closeModal}
          show
          size="tiny"
        >
          {tagCategories.map((category) => {
            const categoryOptions = tags
              .filter((tag) => tag.tag_category_id === category.id)
              .map((tag) => ({ value: tag.id, label: tag.name }))

            return (
              <div className="mb" key={category.id}>
                <label className="label-style">{category.name}</label>
                <Select
                  options={categoryOptions}
                  value={categoryTags[category.id] || null}
                  onChange={(option) => {
                    setCategoryTags((previous) => ({
                      ...previous,
                      [category.id]: option ? option.value : null,
                    }))
                  }}
                  placeholder={`Select ${category.name}...`}
                  isClearable
                />
              </div>
            )
          })}
          {!isEmpty(uncategorizedTagOptions) && (
            <div className="mb">
              <label className="label-style">Uncategorized Tags</label>
              <MultiSelect
                condenseOptions={false}
                showFullOptions
                placeholderPill="Select Tags..."
                options={uncategorizedTagOptions}
                value={uncategorizedTags}
                onChange={(value) => setUncategorizedTags(value)}
              />
            </div>
          )}
          <div className="modal-footer">
            <Button onClick={closeModal}>Cancel</Button>
            <Button
              brand
              disabled={!hasTagsSelected || submitting}
              loading={submitting}
              onClick={handleTagsSubmit}
              data-testid="bulk-tags-apply"
            >
              Apply
            </Button>
          </div>
        </BasicModal>
      )}

      {activeModal === 'playbook' && (
        <BasicModal
          title={`Reassign Playbook for ${pluralize('User', selectedCount, true)}`}
          onClose={closeModal}
          show
          size="tiny"
        >
          <div className="mb">
            <label className="label-style">Select Playbook</label>
            <Select
              options={playbookOptions}
              value={selectedPlaybook}
              onChange={(option) => setSelectedPlaybook(option ? option.value : null)}
              placeholder="Select a playbook..."
              isClearable
            />
          </div>
          <div className="modal-footer">
            <Button onClick={closeModal}>Cancel</Button>
            <Button
              brand
              disabled={!selectedPlaybook || submitting}
              loading={submitting}
              onClick={handlePlaybookSubmit}
              data-testid="bulk-playbook-apply"
            >
              Apply
            </Button>
          </div>
        </BasicModal>
      )}

      {activeModal === 'supportActions' && (
        <SupportActionsModal users={selectedUsers} onClose={closeModal} />
      )}

      {activeModal === 'confirmDeactivate' && (
        <BasicModal
          title={`Deactivate ${selectedCount} Users`}
          onClose={closeModal}
          show
          size="tiny"
        >
          <ModalActions
            onClose={closeModal}
            onSubmit={confirmDeactivateUsers}
            buttonLabel="Deactivate"
            isDelete
          >
            <p>{`Are you sure you want to deactivate ${selectedCount} users? They will be immediately logged out.`}</p>
          </ModalActions>
        </BasicModal>
      )}
    </div>
  )
}
